import { ComponentType, SVGProps } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Stack, styled } from '@mui/material'
import { SparkleIcon } from '@phosphor-icons/react'
import { ClankerCheckImage, ClankerFileImage, ClankerTransformImage, IllustrationCard, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'

export type PlaygroundToolKey = 'val-xml-extractor' | 'pdf-validator' | 'tenant-list-import'

export type PlaygroundToolConfig = {
    key: PlaygroundToolKey
    title: string
    description: string
    to: string
    Image: ComponentType<SVGProps<SVGSVGElement>>
    hoverColor: string
}

type PlaygroundHomeProps = {
    tools: PlaygroundToolKey[]
}

const DEFAULT_DESCRIPTION =
    'Die Tools auf dieser Seite verwenden KI und liefern nicht immer verlässliche Ergebnisse. Antworten bitte sorgfältig prüfen.'

const TenantListImage = styled(ClankerTransformImage)({
    boxSizing: 'border-box',
    width: '175px',
    height: '135px',
    paddingInline: '20px',
    backgroundColor: tokens.ColorGraphSequentialFourLightest,
    borderRadius: tokens.SizingCornerS,
})

export const PLAYGROUND_TOOLS = [
    {
        key: 'val-xml-extractor',
        title: 'PDF zu ValXML',
        description: 'PDF analysieren & ValXML exportieren',
        to: '/playground/val-xml-extractor',
        Image: ClankerFileImage,
        hoverColor: tokens.ColorGraphSequentialSixLightest,
    },
    {
        key: 'pdf-validator',
        title: 'Auto-Plausi',
        description: 'Plausibilitätsprüfung mit KI',
        to: '/playground/pdf-validator',
        Image: ClankerCheckImage,
        hoverColor: tokens.ColorGraphSequentialFiveLightest,
    },
    {
        key: 'tenant-list-import',
        title: 'Mieterliste zu Excel',
        description: 'Mieterliste auslesen & Excel exportieren',
        to: '/playground/tenant-list-import',
        Image: TenantListImage,
        hoverColor: tokens.ColorGraphSequentialFourLightest,
    },
] as const satisfies readonly PlaygroundToolConfig[]

export const PlaygroundHome = ({ tools }: PlaygroundHomeProps) => {
    const navigate = useNavigate()
    const selectedTools = PLAYGROUND_TOOLS.filter((tool) => tools.includes(tool.key))

    return (
        <Stack
            direction="column"
            gap={tokens.SizingSpacing300}
            padding={tokens.SizingSpacing300}
            width="100%"
            maxWidth="1200px"
            margin="0 auto"
        >
            <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                <SparkleIcon size={tokens.SizingIconL} color={tokens.ColorIconBoldest} />
                <Headline variant="headline1" title="Tools" />
            </Stack>
            <Typography variant="textM">{DEFAULT_DESCRIPTION}</Typography>
            <Stack direction="row" flexWrap="wrap" gap={tokens.SizingSpacing300}>
                {selectedTools.map(({ key, title, description, hoverColor, to, Image }) => (
                    <Box key={key} position="relative" width="200px" height="286px">
                        <IllustrationCard
                            title={title}
                            text={description}
                            hoverColor={hoverColor}
                            image={<Image width="100%" height="100%" />}
                            onClick={() => navigate(to)}
                        />
                    </Box>
                ))}
            </Stack>
        </Stack>
    )
}
